/*
 * Stickou — scripts du thème (phase 4).
 * - ajout au panier de plusieurs lignes (Duo, upsell du tiroir) avec mise à jour du tiroir Horizon
 * - interrupteur Coffret / Duo sur la fiche produit
 * - date de livraison estimée (5 à 7 jours ouvrés, hors week-ends et jours fériés FR + BE)
 * - lignes Noël (avant / après le 5 décembre)
 * - bouton collant mobile de l'accueil (masqué quand un bouton principal est visible)
 * - libellé du bouton de commande du tiroir : « Je passe commande — {total} »
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const NL = (document.documentElement.lang || '').toLowerCase().startsWith('nl');

const MSG_ERROR = NL
  ? 'Oeps, dat is niet gelukt. Probeer het zo meteen opnieuw; lukt het nog steeds niet, mail ons dan op contact@cap90.com.'
  : "Oups, ça n'a pas marché. Réessaie dans un instant ; si ça bloque encore, écris-nous à contact@cap90.com.";
const MSG_MAX = NL
  ? 'Je hebt alle beschikbare cadeauboxen in deze kleur toegevoegd. Kies een andere kleur om er meer te geven.'
  : 'Tu as ajouté tous les coffrets disponibles dans ce coloris. Choisis un autre coloris pour en offrir plus.';
const MSG_OFFLINE = NL
  ? 'De verbinding is weggevallen. Controleer je netwerk en probeer het opnieuw.'
  : 'La connexion a sauté. Vérifie ton réseau et réessaie.';

/* ---------------------------------------------------------------- Panier */

function cartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId && !ids.includes(el.dataset.sectionId)) {
      ids.push(el.dataset.sectionId);
    }
  });
  return ids;
}

async function fetchCart() {
  const base = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
  const res = await fetch(`${base}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`cart ${res.status}`);
  return res.json();
}

/**
 * Ajoute des lignes au panier puis laisse Horizon rafraîchir et ouvrir le tiroir.
 * @param {Array<{id: string|number, quantity: number}>} lines
 * @param {HTMLElement} source élément depuis lequel l'événement est émis
 * @returns {Promise<{ok: boolean, message?: string}>}
 */
async function stkAdd(lines, source) {
  const items = lines.filter((l) => l.id && l.quantity > 0);
  if (!items.length) return { ok: false, message: MSG_ERROR };
  const totalQuantity = items.reduce((s, l) => s + l.quantity, 0);
  const target = source || document.body;

  let deferred = null;
  try {
    deferred = CartLinesUpdateEvent.createPromise();
    target.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
        promise: deferred.promise,
      })
    );
  } catch (e) {
    deferred = null;
  }

  const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
  try {
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: items.map((l) => ({ id: Number(l.id), quantity: l.quantity })),
        sections: cartSectionIds().join(','),
      }),
    });
    const data = await res.json();
    const cart = await fetchCart();
    const failed = Boolean(data && data.status);

    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'stk',
          itemCount: totalQuantity,
          sections: failed ? undefined : data.sections,
          didError: failed,
        },
      });
    }
    if (failed) {
      try {
        target.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' }));
      } catch (e) {}
      return { ok: false, message: res.status === 422 ? MSG_MAX : MSG_ERROR };
    }
    if (!deferred) {
      window.location.href = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
    }
    return { ok: true };
  } catch (error) {
    if (deferred) deferred.reject(error);
    return { ok: false, message: navigator.onLine === false ? MSG_OFFLINE : MSG_ERROR };
  }
}

/* ---------------------------------------------------------------- Dates */

function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

const holidayCache = {};
/** Jours fériés France + Belgique (union), au format AAAA-MM-JJ. */
function holidays(year) {
  if (holidayCache[year]) return holidayCache[year];
  const key = (d) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  const easter = easterSunday(year);
  const plus = (n) => new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() + n);
  const set = new Set(
    [
      [1, 1], // Jour de l'an
      [5, 1], // Fête du travail
      [5, 8], // Victoire 1945 (FR)
      [7, 14], // Fête nationale (FR)
      [7, 21], // Fête nationale (BE)
      [8, 15], // Assomption
      [11, 1], // Toussaint
      [11, 11], // Armistice
      [12, 25], // Noël
    ].map(([m, d]) => key(new Date(year, m - 1, d)))
  );
  set.add(key(plus(1))); // Lundi de Pâques
  set.add(key(plus(39))); // Ascension
  set.add(key(plus(50))); // Lundi de Pentecôte
  holidayCache[year] = { set, key };
  return holidayCache[year];
}

function isBusinessDay(d) {
  const day = d.getDay();
  if (day === 0 || day === 6) return false;
  const h = holidays(d.getFullYear());
  return !h.set.has(h.key(d));
}

function addBusinessDays(from, n) {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    if (isBusinessDay(d)) added++;
  }
  return d;
}

function formatDate(d) {
  return d.toLocaleDateString(NL ? 'nl-BE' : 'fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}

function deliveryRange() {
  const now = new Date();
  return { from: addBusinessDays(now, 5), to: addBusinessDays(now, 7) };
}

/** Période Noël : « before » du 1er octobre au 5 décembre inclus, « after » du 6 au 24 décembre. */
function xmasPhase(now = new Date()) {
  const m = now.getMonth() + 1;
  const d = now.getDate();
  if ((m === 10 || m === 11) || (m === 12 && d <= 5)) return 'before';
  if (m === 12 && d <= 24) return 'after';
  return 'none';
}

function applyDates(root = document) {
  const range = deliveryRange();
  root.querySelectorAll('[data-stk-date-from]').forEach((el) => {
    if (el.textContent !== formatDate(range.from)) el.textContent = formatDate(range.from);
  });
  root.querySelectorAll('[data-stk-date-to]').forEach((el) => {
    if (el.textContent !== formatDate(range.to)) el.textContent = formatDate(range.to);
  });
  root.querySelectorAll('[data-stk-range]').forEach((el) => {
    if (el.hidden) el.hidden = false;
  });
  root.querySelectorAll('[data-stk-range-fallback]').forEach((el) => {
    if (!el.hidden) el.hidden = true;
  });
  const phase = xmasPhase();
  root.querySelectorAll('[data-stk-xmas]').forEach((el) => {
    const show = el.getAttribute('data-stk-xmas') === phase;
    if (el.hidden === show) el.hidden = !show;
  });
}

/* ---------------------------------------------------------------- Coffret / Duo */

function textNodeOf(button) {
  for (const node of button.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) return node;
  }
  return null;
}

function initDuo(duo) {
  if (duo.dataset.stkReady) return;
  duo.dataset.stkReady = '1';
  const section = duo.closest('.shopify-section') || document;
  const labels = {
    coffret: duo.dataset.labelCoffret || (NL ? 'Ik geef hem cadeau — € 49,90' : "Je l'offre — 49,90 €"),
    duo: duo.dataset.labelDuo || (NL ? 'Ik geef ze cadeau — € 89,90' : 'Je les offre — 89,90 €'),
    soldOut: duo.dataset.labelSoldOut || (NL ? 'Uitverkocht' : 'Épuisé'),
  };

  const isDuo = () => duo.querySelector('input[value="duo"]')?.checked === true;
  const second = () => section.querySelector('[data-stk-duo-second]');

  const syncLabels = () => {
    const duoOn = isDuo();
    section.querySelectorAll('button[name="add"], sticky-add-to-cart button, .sticky-add-to-cart button').forEach((btn) => {
      const node = textNodeOf(btn);
      if (!node) return;
      const wanted = btn.disabled ? labels.soldOut : duoOn ? labels.duo : labels.coffret;
      if (node.textContent.trim() !== wanted) node.textContent = wanted;
    });
    section.querySelectorAll('variant-picker fieldset legend').forEach((legend) => {
      let prefix = legend.querySelector('.stk-duo-prefix');
      if (duoOn && !prefix) {
        prefix = document.createElement('span');
        prefix.className = 'stk-duo-prefix';
        prefix.textContent = duo.dataset.prefix || (NL ? 'Cadeaubox 1 · ' : 'Coffret 1 · ');
        legend.prepend(prefix);
      } else if (!duoOn && prefix) {
        prefix.remove();
      }
    });
  };

  const syncMode = (fromUser) => {
    const duoOn = isDuo();
    const row = second();
    if (row) {
      if (row.hidden === duoOn) row.hidden = !duoOn;
      if (duoOn && fromUser) {
        // Par défaut, le 2e coffret reprend le coloris du 1er
        const firstId = section.querySelector('product-form-component input[name="id"]')?.value;
        const match = firstId && row.querySelector(`input[value="${firstId}"]:not(:disabled)`);
        if (match) {
          match.checked = true;
          updateSecondName(row);
        }
      }
    }
    section.querySelectorAll('[data-stk-duo-only]').forEach((el) => {
      if (el.hidden === duoOn) el.hidden = !duoOn;
    });
    section.querySelectorAll('[data-stk-coffret-only]').forEach((el) => {
      if (el.hidden !== duoOn) el.hidden = duoOn;
    });
    syncLabels();
  };

  duo.addEventListener('change', () => {
    syncMode(true);
    try {
      const url = new URL(window.location.href);
      if (isDuo()) url.searchParams.set('formule', 'duo');
      else url.searchParams.delete('formule');
      history.replaceState(history.state, '', url);
    } catch (e) {}
  });

  // Les boutons et la légende du sélecteur natif sont re-rendus à chaque changement de coloris
  let pending = false;
  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      syncLabels();
    });
  }).observe(section, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });

  if (new URLSearchParams(window.location.search).get('formule') === 'duo') {
    const input = duo.querySelector('input[value="duo"]');
    if (input) input.checked = true;
  }
  syncMode(true);
}

function updateSecondName(row) {
  const checked = row.querySelector('input[type="radio"]:checked');
  const out = row.querySelector('[data-stk-second-name]');
  if (checked && out) out.textContent = checked.dataset.name || '';
}

function showError(container, message) {
  if (!container) return;
  let box = container.querySelector('.stk-error');
  if (!box) {
    box = document.createElement('p');
    box.className = 'stk-error';
    box.setAttribute('role', 'alert');
    container.appendChild(box);
  }
  box.textContent = message;
  clearTimeout(box._t);
  box._t = setTimeout(() => box.remove(), 10000);
}

// Interception de l'ajout au panier en mode Duo (bouton principal et barre collante)
window.addEventListener(
  'submit',
  async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const component = form.closest('product-form-component');
    if (!component) return;
    const section = component.closest('.shopify-section');
    const duo = section && section.querySelector('[data-stk-duo]');
    if (!duo || duo.querySelector('input[value="duo"]')?.checked !== true) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const firstId = form.querySelector('input[name="id"]')?.value;
    const secondId = section.querySelector('[data-stk-duo-second] input[type="radio"]:checked')?.value;
    const lines =
      !secondId || secondId === firstId
        ? [{ id: firstId, quantity: 2 }]
        : [
            { id: firstId, quantity: 1 },
            { id: secondId, quantity: 1 },
          ];

    const buttons = section.querySelectorAll('button[name="add"]');
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    const result = await stkAdd(lines, component);
    buttons.forEach((b) => b.removeAttribute('aria-busy'));
    if (!result.ok) showError(section.querySelector('[data-stk-duo-second]') || duo, result.message);
  },
  true
);

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  const row = input.closest('[data-stk-duo-second]');
  if (row) updateSecondName(row);
});

/* ---------------------------------------------------------------- Tiroir panier */

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element && event.target.closest('[data-stk-upsell-add]');
  if (!button) return;
  event.preventDefault();
  const box = button.closest('[data-stk-upsell]');
  const id = box && box.querySelector('input[type="radio"]:checked')?.value;
  if (!id) return;
  button.disabled = true;
  const result = await stkAdd([{ id, quantity: 1 }], box);
  button.disabled = false;
  if (!result.ok) showError(box, result.message);
});

function applyCheckoutLabel(root = document) {
  root.querySelectorAll('#checkout, .cart__checkout-button').forEach((btn) => {
    const summary = btn.closest('.cart-drawer__summary, .cart__summary, form, .cart-summary') || document;
    const totalEl =
      summary.querySelector('[data-stk-total]') ||
      document.querySelector('.cart-drawer__summary [data-stk-total]') ||
      document.querySelector('[data-stk-total]');
    const total = totalEl ? totalEl.getAttribute('data-stk-total') : '';
    const cta = NL ? 'Bestellen' : 'Je passe commande';
    const wanted = total ? `${cta} — ${total}` : cta;
    const label = btn.querySelector('.button-text') || btn;
    if (label.children.length === 0 && label.textContent.trim() !== wanted) label.textContent = wanted;
  });
}

/* ---------------------------------------------------------------- Bouton collant (accueil) */

function initSticky() {
  const sticky = document.querySelector('[data-stk-sticky]');
  if (!sticky || !('IntersectionObserver' in window)) {
    if (sticky) sticky.classList.remove('is-hidden');
    return;
  }
  const targets = Array.from(
    document.querySelectorAll('main a.button:not(.button-secondary):not(.button-unstyled), main .stk-btn--primary')
  ).filter((el) => !el.closest('[data-stk-sticky]'));
  const visible = new Set();
  let started = false;
  const update = () => {
    const hide = visible.size > 0 || !started;
    sticky.classList.toggle('is-hidden', hide);
    sticky.setAttribute('aria-hidden', hide ? 'true' : 'false');
    sticky.tabIndex = hide ? -1 : 0;
  };
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
      update();
    },
    { threshold: 0 }
  );
  targets.forEach((t) => io.observe(t));
  // Le bouton n'apparaît qu'après le héros
  const start = () => {
    if (!started && window.scrollY > 200) {
      started = true;
      update();
    }
  };
  window.addEventListener('scroll', start, { passive: true });
  start();
  update();
}

/* ---------------------------------------------------------------- Préférences cookies */

// Lien « Préférences cookies » du pied de page : rouvre la bannière de cookies Shopify
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element && event.target.closest('[data-stk-cookie-prefs], a[href="#preferences-cookies"]');
  if (!link) return;
  event.preventDefault();
  const open = () => {
    if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
      window.privacyBanner.showPreferences();
      return true;
    }
    return false;
  };
  if (open()) return;
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
      if (!open()) window.location.href = '/policies/privacy-policy';
    });
  } else {
    window.location.href = '/policies/privacy-policy';
  }
});

/* ---------------------------------------------------------------- Démarrage */

function refresh(root = document) {
  applyDates(root);
  root.querySelectorAll('[data-stk-duo]').forEach(initDuo);
  applyCheckoutLabel(root);
}

refresh();
initSticky();

// Le tiroir panier et les sections sont re-rendus par Horizon : on réapplique dates et libellés
let scheduled = false;
new MutationObserver(() => {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    refresh();
  });
}).observe(document.body, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', () => refresh());
